/**
 * SearchableSelect — accessible combobox with type-ahead search.
 * Drop-in replacement for `<select>` when the option list is long enough that
 * scrolling is painful (projects, employees, vendors, etc.).
 *
 * Usage:
 *   <SearchableSelect
 *     value={form.project_id}
 *     onChange={(id, item) => update('project_id', id)}
 *     options={projects}
 *     getKey={(p) => p.id}
 *     getLabel={(p) => `${p.code} · ${p.name}`}
 *     getSearchTokens={(p) => [p.code, p.name, p.site_code]}
 *     placeholder="Search by site code or project name…"
 *     emptyValueLabel="Head Office"
 *     data-testid="project-search-select"
 *   />
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';

export default function SearchableSelect({
  value,
  onChange,
  options = [],
  getKey,
  getLabel,
  getSearchTokens,
  placeholder = 'Search…',
  emptyValueLabel = null,        // when set, an explicit "no selection" option is rendered with this label
  emptyValueDescription = null,
  className = '',
  disabled = false,
  ...rest
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(
    () => options.find((o) => getKey(o) === value) || null,
    [options, value, getKey]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 100); // cap render
    return options
      .filter((o) => {
        const tokens = (getSearchTokens ? getSearchTokens(o) : [getLabel(o)])
          .filter(Boolean)
          .map((t) => String(t).toLowerCase());
        return tokens.some((t) => t.includes(q));
      })
      .slice(0, 100);
  }, [options, query, getLabel, getSearchTokens]);

  // close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // focus input when opening
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const pick = (item) => {
    onChange(item ? getKey(item) : '', item);
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={containerRef} className={`relative ${className}`} {...rest}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 border rounded-sm text-sm bg-white text-left ${
          disabled ? 'bg-slate-50 text-slate-400 cursor-not-allowed' : 'hover:border-[#1F7A6E]'
        } ${open ? 'border-[#1F7A6E] ring-1 ring-[#1F7A6E]' : 'border-slate-300'}`}
      >
        <span className={selected ? 'text-slate-900 truncate' : 'text-slate-400 truncate'}>
          {selected ? getLabel(selected) : (emptyValueLabel || placeholder)}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {selected && !disabled && (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => { e.stopPropagation(); pick(null); }}
              className="p-0.5 hover:bg-slate-200 rounded-sm"
              aria-label="Clear selection"
            >
              <X size={12} className="text-slate-400" />
            </span>
          )}
          <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg overflow-hidden">
          <div className="border-b border-slate-200 px-2.5 py-2 flex items-center gap-1.5 bg-slate-50">
            <Search size={12} className="text-slate-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              className="flex-1 bg-transparent text-sm outline-none"
              onKeyDown={(e) => {
                if (e.key === 'Escape') setOpen(false);
                if (e.key === 'Enter' && filtered.length > 0) pick(filtered[0]);
              }}
            />
            {query && (
              <button type="button" onClick={() => setQuery('')} className="text-slate-400 hover:text-slate-700">
                <X size={12} />
              </button>
            )}
          </div>
          <ul className="max-h-64 overflow-y-auto py-1 text-sm">
            {emptyValueLabel && (
              <li>
                <button
                  type="button"
                  onClick={() => pick(null)}
                  className={`w-full text-left px-3 py-2 hover:bg-teal-50 flex flex-col ${!value ? 'bg-teal-50/60' : ''}`}
                >
                  <span className="text-slate-700 italic">{emptyValueLabel}</span>
                  {emptyValueDescription && <span className="text-[10px] text-slate-400">{emptyValueDescription}</span>}
                </button>
              </li>
            )}
            {filtered.length === 0 ? (
              <li className="px-3 py-4 text-center text-xs text-slate-400">
                No matches for &quot;{query}&quot;
              </li>
            ) : (
              filtered.map((item) => {
                const k = getKey(item);
                return (
                  <li key={k}>
                    <button
                      type="button"
                      onClick={() => pick(item)}
                      className={`w-full text-left px-3 py-2 hover:bg-teal-50 ${
                        k === value ? 'bg-teal-50/60 font-semibold text-[#1F7A6E]' : 'text-slate-700'
                      }`}
                    >
                      {getLabel(item)}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
          {options.length > 100 && !query && (
            <div className="px-3 py-1.5 border-t border-slate-200 text-[10px] text-slate-400 bg-slate-50">
              Showing first 100 of {options.length}. Start typing to narrow down.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
